<template>
  <b-modal
    :visible="visible"
    size="xl"
    centered
    scrollable
    no-close-on-backdrop
    body-class="p-0"
    dialog-class="wl-treatment-modal-dialog"
    content-class="wl-treatment-modal-content"
    @change="val => $emit('update:visible', val)"
    @hidden="onHidden">
    <template #modal-header="{ close }">
      <div class="w-100">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h4 class="modal-title mb-1">
              {{ editId ? 'Edit treatment' : 'New GLP-1 treatment' }}
            </h4>
            <p class="text-muted mb-0 small">
              Configure the treatment and all its doses in one step. Doses define label, quantity per injection, and inventory item to deduct.
            </p>
          </div>
          <button type="button" class="close" @click="close">
            <span>&times;</span>
          </button>
        </div>
      </div>
    </template>

    <b-container fluid class="py-3 px-4">
      <b-row>
        <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
          <b-card class="h-100 shadow-sm border-0 bg-light">
            <h6 class="text-primary mb-3">
              <i class="fas fa-syringe mr-2"></i>Treatment details
            </h6>
            <b-form-group label="Name" label-class="font-weight-bold">
              <b-form-input
                v-model="localForm.name"
                placeholder="E.g. Tirzepatide + B12"
                size="lg" />
            </b-form-group>
            <b-form-group label="Description" label-class="font-weight-bold">
              <b-form-textarea
                v-model="localForm.description"
                rows="4"
                placeholder="Description shown in listings and reports" />
            </b-form-group>
            <b-form-group label-class="font-weight-bold">
              <b-form-checkbox v-model="localForm.active" switch>
                Active treatment
              </b-form-checkbox>
            </b-form-group>
            <b-alert show variant="info" class="small mb-0">
              <strong>{{ sortedDoses.length }}</strong>
              {{ sortedDoses.length === 1 ? 'dose configured' : 'doses configured' }}
            </b-alert>
          </b-card>
        </b-col>

        <b-col cols="12" lg="8">
          <b-card class="shadow-sm border-0 h-100">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <h6 class="text-primary mb-0">
                <i class="fas fa-list-ol mr-2"></i>Dose schedule
              </h6>
              <b-button variant="primary" size="sm" @click="addDoseRow">
                <i class="fas fa-plus mr-1"></i> Add dose
              </b-button>
            </div>

            <b-alert v-if="!localForm.doses.length" show variant="warning" class="mb-3">
              Add at least one dose with a label (e.g. 2.5 mg, 5.0 mg).
            </b-alert>

            <div class="table-responsive wl-doses-table-wrap">
              <b-table
                :items="sortedDoses"
                :fields="doseFields"
                small
                bordered
                hover
                fixed
                show-empty
                empty-text="No doses — use 'Add dose'">
                <template #cell(sort_order)="data">
                  <b-form-input
                    v-model.number="data.item.sort_order"
                    type="number"
                    min="1"
                    class="text-center dose-input" />
                </template>
                <template #cell(dose_label)="data">
                  <b-form-input
                    v-model="data.item.dose_label"
                    placeholder="2.5 mg"
                    class="dose-input" />
                </template>
                <template #cell(qty_per_injection)="data">
                  <b-form-input
                    v-model="data.item.qty_per_injection"
                    type="number"
                    step="0.0001"
                    min="0"
                    class="dose-input text-center" />
                </template>
                <template #cell(inv_item_id)="data">
                  <treeselect
                    v-model="data.item.inv_item_id"
                    :options="itemOptions"
                    :clearable="true"
                    placeholder="Item"
                    class="dose-treeselect" />
                </template>
                <template #cell(active)="data">
                  <b-form-checkbox v-model="data.item.active" switch class="d-flex justify-content-center" />
                </template>
                <template #cell(actions)="data">
                  <b-button
                    size="sm"
                    variant="outline-danger"
                    v-b-tooltip.hover
                    title="Remove dose"
                    @click="removeDoseRow(data.item._key)">
                    <i class="fas fa-trash-alt"></i>
                  </b-button>
                </template>
              </b-table>
            </div>

            <b-alert show variant="light" class="small mt-3 mb-0">
              <i class="fas fa-info-circle text-primary mr-1"></i>
              When a visit is marked with injection, the system deducts stock from the
              <strong>linked item</strong> × quantity per injection.
            </b-alert>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <template #modal-footer>
      <b-button variant="outline-secondary" :disabled="loading" @click="closeModal">
        Cancel
      </b-button>
      <b-button variant="primary" :disabled="loading" @click="submit">
        <SpinnerLoading v-if="loading" />
        <span v-else>
          <i class="fas fa-save mr-1"></i>
          {{ editId ? 'Save changes' : 'Create treatment' }}
        </span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BContainer,
  BRow,
  BCol,
  BCard,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BAlert,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import SpinnerLoading from '@/components/SpinnerLoading'

export function newDoseRow(sortOrder = 1) {
  return {
    _key: `dose-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    id: null,
    dose_label: '',
    qty_per_injection: 1,
    sort_order: sortOrder,
    inv_item_id: null,
    active: true,
  }
}

export function mapDoseFromApi(dose, index) {
  return {
    _key: `dose-${dose.id || index}`,
    id: dose.id,
    dose_label: dose.dose_label || '',
    qty_per_injection: dose.qty_per_injection ?? 1,
    sort_order: dose.sort_order ?? index + 1,
    inv_item_id: dose.inv_item_id,
    active: dose.active !== false,
  }
}

export default {
  name: 'TreatmentFormModal',
  components: {
    BModal,
    BContainer,
    BRow,
    BCol,
    BCard,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BAlert,
    BTable,
    Treeselect,
    SpinnerLoading,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    editId: { default: null },
    form: { type: Object, required: true },
    itemOptions: { type: Array, default: () => [] },
    removedDoseIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      localForm: { name: '', description: '', active: true, doses: [] },
      localRemovedIds: [],
      doseFields: [
        { key: 'sort_order', label: '#', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '70px' } },
        { key: 'dose_label', label: 'Dose label', thStyle: { minWidth: '120px' } },
        { key: 'qty_per_injection', label: 'Qty / injection', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '110px' } },
        { key: 'inv_item_id', label: 'Inventory item', thStyle: { minWidth: '200px' } },
        { key: 'active', label: 'Active', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '80px' } },
        { key: 'actions', label: '', thStyle: { width: '56px' } },
      ],
    }
  },
  computed: {
    sortedDoses() {
      return [...this.localForm.doses].sort(
        (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0)
      )
    },
  },
  watch: {
    visible(open) {
      if (open) this.syncFromProps()
    },
    form: {
      deep: true,
      handler() {
        if (this.visible) this.syncFromProps()
      },
    },
  },
  methods: {
    syncFromProps() {
      this.localForm = {
        name: this.form.name,
        description: this.form.description,
        active: this.form.active,
        doses: this.form.doses.map(d => ({ ...d })),
      }
      this.localRemovedIds = [...this.removedDoseIds]
    },
    addDoseRow() {
      const nextOrder = this.localForm.doses.length
        ? Math.max(...this.localForm.doses.map(d => Number(d.sort_order) || 0)) + 1
        : 1
      this.localForm.doses.push(newDoseRow(nextOrder))
    },
    removeDoseRow(key) {
      const idx = this.localForm.doses.findIndex(d => d._key === key)
      if (idx === -1) return
      const row = this.localForm.doses[idx]
      if (row.id) this.localRemovedIds.push(row.id)
      this.localForm.doses.splice(idx, 1)
    },
    closeModal() {
      this.$emit('update:visible', false)
    },
    onHidden() {
      this.$emit('close')
    },
    submit() {
      this.$emit('save', {
        form: {
          name: this.localForm.name,
          description: this.localForm.description,
          active: this.localForm.active,
          doses: this.localForm.doses.map(d => ({ ...d })),
        },
        removedDoseIds: [...this.localRemovedIds],
      })
    },
  },
}
</script>

<style scoped>
::v-deep .wl-treatment-modal-dialog {
  max-width: 1140px;
}
::v-deep .wl-treatment-modal-content {
  border-radius: 0.5rem;
}
.wl-doses-table-wrap {
  max-height: 420px;
  overflow-y: auto;
}
::v-deep .dose-input {
  min-width: 0;
}
::v-deep .dose-treeselect .vue-treeselect__control {
  min-height: 32px;
  font-size: 0.875rem;
}
</style>
